import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { keepOptionVisible } from './keepOptionVisible'

/** What a picker holds: a record's id, and the name it goes by. */
export interface PickedRecord {
  id: string
  name: string
}

/**
 * Asks a book for the records whose name or MC number holds `term` — at most `limit`, the
 * ones that start with it first. It must keep its identity between renders (a module-level
 * function): the picker asks again whenever it changes.
 */
export type RecordSearch<T extends PickedRecord> = (term: string, limit: number) => Promise<T[]>

/** How many records a picker lists. It asks one more, to know when more match. */
export const PICKER_SHOWN = 10

/** Long enough for a typist's next key, short enough that the list keeps up. */
const TYPING_PAUSE_MS = 150

type Answer<T> = { term: string; records: T[] } | { term: string; failed: true }

type Option<T> = { kind: 'none' } | { kind: 'record'; record: T; suggested: boolean }

/** The key the highlight holds for the option that chooses nobody; a record's is its id. */
const NOBODY = ':nobody'

function keyOf(option: Option<PickedRecord>): string {
  return option.kind === 'none' ? NOBODY : option.record.id
}

const nameOf = (record: PickedRecord) => record.name

/**
 * Chooses one record of a book — a carrier, a broker — by searching it (A1, 2026-09-28).
 *
 * <p>The value is a record, never the text: the box only searches, and leaving it without
 * choosing puts the choice's name back. So it cannot hold a half-typed name the form would
 * send, and a record past the first 500 of a book is as reachable as the first. It opens on a
 * click or an arrow key with the first ten names — a small book reads like the select it
 * replaced — and narrows as the dispatcher types, asking the server after a short pause; only
 * the answer to what the box says now is ever chosen from.</p>
 *
 * <p>The mechanics are the city typeahead's, proven on the road: an APG 1.2 combobox with ids
 * from useId, a highlight that wraps and scrolls inside its own popup, a live region that is
 * always mounted, mousedown kept from blurring the box, and a settling guard — while an answer
 * is on its way, keys and clicks commit nothing from the list still showing.</p>
 */
export function RecordPicker<T extends PickedRecord>({
  name,
  label,
  value,
  onChange,
  search,
  suggested = [],
  describe,
  textOf = nameOf,
  noneLabel,
  placeholder,
  error,
  required = false,
  disabled = false,
}: {
  name: string
  label: string
  /** The record chosen, or null for none. */
  value: T | null
  onChange: (record: T | null) => void
  search: RecordSearch<T>
  /** Records to offer first — the brokers the paste's answer named. */
  suggested?: readonly T[]
  /** A second line under a record's name: where it is, its MC number, its standing. */
  describe?: (record: T) => string | undefined
  /** What the box says for the chosen record; its name unless told otherwise. */
  textOf?: (record: T) => string
  /** An option that chooses nothing — "Any carrier" — for the pickers that may be left empty. */
  noneLabel?: string
  placeholder?: string
  error?: ReactNode
  required?: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const listboxId = useId()
  const errorId = `${name}-error`
  const popupRef = useRef<HTMLDivElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  // What was typed since the box was last left; null while the box shows the choice.
  const [typed, setTyped] = useState<string | null>(null)
  const [answer, setAnswer] = useState<Answer<T> | null>(null)
  // The highlight holds a record, never a position: on the paste, the book's suggestions can
  // land above the list while it is open, and the same position then held another broker,
  // which Enter filed the loads under (GPT-5.6 Sol on #42).
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const chosenText = value === null ? '' : textOf(value)
  const term = (typed ?? '').trim()

  useEffect(() => {
    if (!isOpen) {
      return
    }

    let active = true
    // Opening asks at once; typing waits for a pause, so a name typed in one go is one ask.
    const timer = window.setTimeout(
      () => {
        search(term, PICKER_SHOWN + 1)
          .then((records) => {
            if (active) {
              setAnswer({ term, records })
              setActiveKey(null)
            }
          })
          .catch(() => {
            if (active) {
              setAnswer({ term, failed: true })
              setActiveKey(null)
            }
          })
      },
      term === '' ? 0 : TYPING_PAUSE_MS,
    )

    // Only the latest ask may answer: a slower answer to what the box said before would put
    // somebody else's records under what it says now.
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [isOpen, term, search])

  // A picker disabled while open — its form busy, its fleet on the way — closes, as leaving
  // it does: its answer goes with the opening that asked for it.
  useEffect(() => {
    if (disabled) {
      setIsOpen(false)
      setTyped(null)
      setActiveKey(null)
      setAnswer(null)
    }
  }, [disabled])

  // An answer about another term is still on screen while this one is asked: nothing on it
  // may be chosen, by key or by click, until the answer to what the box says has landed.
  const isSettling = isOpen && (answer === null || answer.term !== term)
  const failed = answer !== null && answer.term === term && 'failed' in answer
  const records = answer !== null && 'records' in answer ? answer.records : []

  const needle = term.toLowerCase()
  const offered = suggested.filter((record) => record.name.toLowerCase().includes(needle))
  const found = records.slice(0, PICKER_SHOWN).filter((record) => !offered.some((known) => known.id === record.id))
  const more = records.length > PICKER_SHOWN

  const options: Option<T>[] = [
    ...(noneLabel !== undefined && term === '' ? [{ kind: 'none' as const }] : []),
    ...offered.map((record) => ({ kind: 'record' as const, record, suggested: true })),
    ...found.map((record) => ({ kind: 'record' as const, record, suggested: false })),
  ]
  const recordCount = offered.length + found.length

  // Where the highlighted record sits now; nowhere once it has left the list, which takes the
  // highlight with it rather than handing it to whatever moved into its place.
  const highlighted = activeKey === null ? -1 : options.findIndex((option) => keyOf(option) === activeKey)
  const activeIndex = highlighted === -1 ? null : highlighted

  // Only what the book answered can be stale: choosing nobody, or a record the page itself
  // suggested, means the same whatever the answer on screen belongs to.
  const canCommit = (option: Option<T>) => !isSettling || option.kind === 'none' || option.suggested

  useEffect(() => {
    if (activeIndex === null) {
      return
    }
    const popup = popupRef.current
    const option = document.getElementById(`${listboxId}-option-${activeIndex}`)
    if (popup !== null && option !== null) {
      popup.scrollTop = keepOptionVisible(popup, option)
    }
  }, [activeIndex, listboxId])

  function optionId(index: number): string {
    return `${listboxId}-option-${index}`
  }

  // A disabled box takes no click, key or change, so nothing here asks whether it is.
  function open() {
    setIsOpen(true)
  }

  /** Closes the list and puts the choice's name back in the box. */
  function leave() {
    setIsOpen(false)
    setTyped(null)
    setActiveKey(null)
    // An answer belongs to the opening that asked for it. Kept, a list reopened on the same
    // empty box offered the last opening's records — clickable — while this one's were still
    // on their way, from a book that may have changed since (Copilot on #42).
    setAnswer(null)
  }

  function commit(option: Option<T>) {
    const next = option.kind === 'none' ? null : option.record

    // Choosing what is already chosen changes nothing, as with a select: the posting form
    // empties its unit boxes on a new carrier, and the same one again is not a new one.
    if ((next?.id ?? null) !== (value?.id ?? null)) {
      onChange(next)
    }

    leave()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!isOpen) {
        open()
        return
      }
      if (isSettling || options.length === 0) {
        return
      }
      const last = options.length - 1
      const next =
        event.key === 'ArrowDown'
          ? activeIndex === null || activeIndex === last
            ? 0
            : activeIndex + 1
          : activeIndex === null || activeIndex === 0
            ? last
            : activeIndex - 1
      setActiveKey(keyOf(options[next]!))
    } else if (event.key === 'Enter') {
      if (isOpen && activeIndex !== null) {
        event.preventDefault()
        const option = options[activeIndex]
        if (option !== undefined && canCommit(option)) {
          commit(option)
        }
        return
      }
      // The box says what was typed, not what is chosen: Enter never submits a form over it.
      if (typed !== null) {
        event.preventDefault()
      }
    } else if (event.key === 'Escape') {
      if (isOpen || typed !== null) {
        event.preventDefault()
        leave()
      }
    } else if (event.key === 'Tab') {
      leave()
    }
  }

  const status = failed
    ? t('picker.failed')
    : recordCount > 0
      ? null
      : isSettling
        ? t('picker.searching')
        : t('picker.noMatches')

  return (
    <div className="field field--picker">
      <label htmlFor={name}>{label}</label>
      <div className="typeahead picker">
        <input
          id={name}
          name={name}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={isOpen && activeIndex !== null ? optionId(activeIndex) : undefined}
          aria-required={required}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : errorId}
          value={typed ?? chosenText}
          // A click empties the box to search afresh, and the choice waits in the placeholder:
          // typed after the name, a search for "Coyote Logisticsla" would find nobody.
          placeholder={typed === '' && value !== null ? chosenText : placeholder}
          disabled={disabled}
          onFocus={(event) => event.currentTarget.select()}
          onClick={() => {
            if (!isOpen) {
              setTyped('')
              open()
            }
          }}
          onChange={(event) => {
            setTyped(event.target.value)
            setActiveKey(null)
            open()
          }}
          onBlur={leave}
          onKeyDown={handleKeyDown}
        />
        {isOpen && (
          // Mousedown anywhere in the popup keeps the box focused, so its blur cannot close
          // the list before a click lands — the hand-rolled combobox's classic trap.
          <div className="typeahead__popup" ref={popupRef} onMouseDown={(event) => event.preventDefault()}>
            {/* Busy while it still shows an answer to something the box no longer says. */}
            <ul role="listbox" id={listboxId} className="typeahead__list" aria-label={label} aria-busy={isSettling}>
              {options.map((option, index) => (
                <li
                  key={option.kind === 'none' ? '' : option.record.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={
                    index === activeIndex ? 'typeahead__option typeahead__option--active' : 'typeahead__option'
                  }
                  onClick={() => {
                    if (canCommit(option)) {
                      commit(option)
                    }
                  }}
                >
                  {option.kind === 'none' ? (
                    noneLabel
                  ) : (
                    <>
                      <Highlighted text={option.record.name} needle={needle} />
                      <Detail
                        parts={[option.suggested ? t('picker.suggested') : undefined, describe?.(option.record)]}
                      />
                    </>
                  )}
                </li>
              ))}
            </ul>
            {status !== null && <p className="typeahead__empty">{status}</p>}
            {more && !failed && <p className="typeahead__more">{t('picker.more')}</p>}
          </div>
        )}
      </div>
      {/* Always mounted: a live region that appears and disappears announces nothing. */}
      <span className="visually-hidden" aria-live="polite">
        {isOpen && !isSettling && !failed && recordCount > 0
          ? `${t('picker.results', { count: recordCount })}${more ? ` ${t('picker.more')}` : ''}`
          : ''}
      </span>
      {error !== undefined && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

/** The part of the name the search matched, in bold. */
function Highlighted({ text, needle }: { text: string; needle: string }) {
  const at = needle === '' ? -1 : text.toLowerCase().indexOf(needle)

  if (at === -1) {
    return <>{text}</>
  }

  return (
    <>
      {text.slice(0, at)}
      <strong>{text.slice(at, at + needle.length)}</strong>
      {text.slice(at + needle.length)}
    </>
  )
}

function Detail({ parts }: { parts: ReadonlyArray<string | undefined> }) {
  const shown = parts.filter((part): part is string => part !== undefined && part !== '')
  return shown.length === 0 ? null : <span className="typeahead__detail">{shown.join(' · ')}</span>
}
